import React, { useEffect, useState } from 'react';
import { ShieldCheck, Terminal, Trash2, ArrowUpRight, Lock, Activity, ShieldAlert } from 'lucide-react';
import { useSessionStore } from '../../store/useSessionStore';

export const ContextBar: React.FC = () => {
  const [utcTime, setUtcTime] = useState<string>('');
  const [purgeNotice, setPurgeNotice] = useState<string | null>(null);

  const {
    sessionToken,
    nodeId,
    encryptionStandard,
    destroySession,
    lastPurgeReason,
    purgeCount,
  } = useSessionStore();

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const iso = now.toISOString().replace('T', ' ').substring(0, 19) + ' UTC';
      setUtcTime(iso);
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleManualPurge = () => {
    destroySession('Manual Terminal Purge');
    setPurgeNotice('RAM ОЧИЩЕНА // ZERO-TRACE ПРИМЕНЕН');
    setTimeout(() => setPurgeNotice(null), 3000);
  };

  return (
    <aside
      aria-label="Bloomberg Terminal Security Context & Status Bar"
      className="w-full bg-[#F7F4EE] text-[#5C5853] border-t border-[#EAE5DE] py-3 text-xs font-mono select-none z-40 relative shadow-sm"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4">
        {/* Left: Terminal Node & Encryption */}
        <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
          <div className="flex items-center gap-2 text-[#141416]">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-600"></span>
            </span>
            <span className="font-semibold tracking-wider text-[11px] text-[#141416]">
              S&P SECURE NODE:
            </span>
            <span className="text-[#8C6D37] text-[11px] font-semibold">{nodeId}</span>
          </div>

          <div className="hidden sm:flex items-center gap-1.5 border-l border-[#EAE5DE] pl-3 text-[11px]">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-[#141416] font-medium">{encryptionStandard}</span>
          </div>

          <div className="hidden md:flex items-center gap-1.5 border-l border-[#EAE5DE] pl-3 text-[11px] text-[#5C5853]">
            <Lock className="w-3 h-3 text-[#8C6D37]" />
            <span className="font-medium">152-ФЗ / 98-ФЗ КОММЕРЧЕСКАЯ ТАЙНА</span>
          </div>
        </div>

        {/* Center: Live UTC + Radar Snapshot */}
        <div className="hidden lg:flex items-center gap-4 text-[11px]">
          <div className="flex items-center gap-1.5 text-[#5C5853]">
            <Terminal className="w-3.5 h-3.5 text-[#8C6D37]" />
            <span>UTC:</span>
            <span className="text-[#141416] font-semibold tracking-wider">{utcTime || 'SYNCHRONIZING...'}</span>
          </div>
          <div className="text-[#5C5853] border-l border-[#EAE5DE] pl-4 flex items-center gap-1.5">
            <Activity className="w-3 h-3 text-emerald-600" />
            <span>M&A RADAR:</span>
            <span className="text-emerald-700 font-semibold">14 СТРАТЕГОВ В АКТИВНОМ ПОИСКЕ</span>
          </div>
        </div>

        {/* Right: Ephemeral Session Token, Purge & Direct CTA */}
        <div className="flex items-center gap-3 text-[11px]">
          {purgeNotice ? (
            <span className="text-emerald-700 font-semibold animate-pulse text-[11px]">
              {purgeNotice}
            </span>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-[#5C5853]">SESSION:</span>
              <span className="text-[#8C6D37] bg-white px-2 py-0.5 rounded-[2px] border border-[#EAE5DE] font-semibold shadow-2xs">
                {sessionToken}
              </span>
              {purgeCount > 0 && (
                <span className="text-[10px] text-[#5C5853] hidden sm:inline" title={lastPurgeReason || ''}>
                  (WIPES: {purgeCount})
                </span>
              )}
            </div>
          )}

          <button
            onClick={handleManualPurge}
            title="Немедленно уничтожить сессию и очистить оперативную память (Zero-Trace Protocol)"
            className="flex items-center gap-1 bg-white hover:bg-rose-50 hover:text-rose-700 text-[#141416] border border-[#EAE5DE] hover:border-rose-300 px-2.5 py-1 rounded-[2px] transition-colors cursor-pointer text-[10px] font-semibold shadow-2xs"
          >
            <Trash2 className="w-3 h-3 text-rose-600" />
            <span className="hidden sm:inline">ZERO-TRACE WIPE</span>
          </button>

          <a
            href="#vulnerability-scoring"
            className="hidden sm:inline-flex items-center gap-1 bg-[#8C6D37] hover:bg-[#9A7B3E] text-white px-3 py-1 rounded-[2px] text-[10px] font-semibold tracking-wider transition-colors shadow-2xs"
          >
            <span>M&A-SCORING</span>
            <ArrowUpRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </aside>
  );
};

